import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { TRoutes } from '../../shared/const'
import usePrefersReducedMotion from '../../shared/usePrefersReducedMotion'

import kursAvif from '../../assets/images/konzept/kapitel-01.avif'
import kursWebp from '../../assets/images/konzept/kapitel-01.webp'
import messeAvif from '../../assets/images/bilanz/messe.avif'
import messeWebp from '../../assets/images/bilanz/messe.webp'

import styles from './style.module.scss'

/**
 * Der Aufmacher der öffentlichen Startseite — seit 25.08.2026 mit zwei
 * Nachrichten statt einer.
 *
 * Er hieß bis dahin `CourseBanner` und zeigte nur Skills4Retail. Dazu kommt die
 * **Junior-Bilanz 2025/26**: Wer die Startseite öffnet, soll sehen, dass es hier
 * etwas zu lernen gibt *und* was im letzten Schuljahr entstanden ist. Zwei
 * Aufmacher untereinander hätten die Firmenkarten unter den Bildschirmrand
 * geschoben — deshalb einer, der wechselt.
 *
 * **Dezent heißt hier: kein Bewegen, nur ein Überblenden.** Nichts fährt herein,
 * nichts springt; die eine Nachricht wird weich von der anderen abgelöst, alle
 * neun Sekunden. Wer die Maus darüber hält oder mit der Tastatur hineingeht,
 * hält den Wechsel an — sonst wechselt die Nachricht unter dem Zeiger weg,
 * während man sie liest. Die Punkte darunter sind der Weg von Hand.
 *
 * `prefers-reduced-motion` schaltet den selbsttätigen Wechsel **ganz ab** und
 * zeigt die erste Nachricht: Ein Wechsel ist Bewegung, auch ohne Animation.
 * Beide Nachrichten bleiben über die Punkte erreichbar.
 *
 * Das Foto auf der Bilanz-Nachricht ist das erste Bild aus dem Bericht selbst
 * (`bilder/b1.jpg` im Bauwerkzeug). Es ist — wie alle Bilder des Berichts —
 * **KI-generiert und trägt den Hinweis**; ohne ihn stünde hier ein Foto, das
 * niemand als erzeugt erkennt.
 */

/* Neun Sekunden: lang genug, um den Absatz zu lesen, kurz genug, dass die
   zweite Nachricht nicht erst nach dem Wegscrollen erscheint. */
const WECHSEL_MS = 9000

export default function NewsBanner() {
	const { t } = useTranslation()
	const [aktiv, setAktiv] = useState(0)
	const [angehalten, setAngehalten] = useState(false)
	const wenigerBewegung = usePrefersReducedMotion()

	const NACHRICHTEN = [
		{
			key: 'kurs',
			kicker: t('Free · no account needed'),
			titel: t('Skills4Retail — your online shop course'),
			text: t(
				'The turbo for your Junior Company: how an online shop earns money, how customers find you, what you sell and where you get it, payment, shipping, marketing. Test yourself after every chapter.'
			),
			cta: t('Start the course'),
			ziel: TRoutes.PUBLIC_COURSE,
			meta: t('10 chapters · 5 questions each · about 15 minutes per chapter'),
			punkt: t('Skills4Retail'),
			bild: { avif: kursAvif, webp: kursWebp },
			marke: 'Cross- & Up-Sell'
		},
		{
			key: 'bilanz',
			kicker: t('Annual report · school year 2025/26'),
			titel: t('Junior-Bilanz 2025/26'),
			text: t(
				'592 Junior Companies, 4,966 students at 185 schools in 9 federal states — and 281 companies that made it all the way to the certificate. The public edition shows what a school year of Junior moves.'
			),
			cta: t('Read the report'),
			ziel: TRoutes.PUBLIC_ANNUAL_REPORT,
			meta: t('Public edition · without financial results · excl. Junior Mini Companies'),
			punkt: t('Junior-Bilanz 2025/26'),
			bild: { avif: messeAvif, webp: messeWebp },
			marke: '592 Junior Companies',
			kiHinweis: true
		}
	]

	/*
	 * Der Zeitgeber steht still, solange jemand über dem Aufmacher ist — und er
	 * wird bei jedem Wechsel neu gestellt. Ohne das Neustellen käme die nächste
	 * Nachricht nach einem Klick auf einen Punkt womöglich schon nach einer
	 * Sekunde.
	 */
	const anzahl = NACHRICHTEN.length
	const anzahlRef = useRef(anzahl)
	anzahlRef.current = anzahl

	useEffect(() => {
		if (wenigerBewegung || angehalten) return undefined

		const uhr = setTimeout(() => setAktiv(i => (i + 1) % anzahlRef.current), WECHSEL_MS)

		return () => clearTimeout(uhr)
	}, [aktiv, angehalten, wenigerBewegung])

	const nachricht = NACHRICHTEN[aktiv]

	return (
		<section
			className={styles.root}
			onMouseEnter={() => setAngehalten(true)}
			onMouseLeave={() => setAngehalten(false)}
			onFocus={() => setAngehalten(true)}
			onBlur={() => setAngehalten(false)}
			aria-label={t('News')}
		>
			{/* Zwei weiche Lichtflecken hinter dem Inhalt, rein dekorativ. */}
			<i className={styles.blobOne} aria-hidden="true" />
			<i className={styles.blobTwo} aria-hidden="true" />

			{/*
			 * `key` an der Nachricht: React tauscht den Block aus, und die
			 * Einblend-Animation läuft von vorn. Ein Umschalten von Klassen an
			 * demselben Element würde die Animation nicht neu starten.
			 *
			 * `aria-live="polite"` sagt der Vorlesehilfe Bescheid, dass sich hier
			 * etwas von selbst ändert — sonst wechselt die Nachricht lautlos.
			 */}
			<div key={nachricht.key} className={styles.slide} aria-live="polite">
				<div className={styles.text}>
					<span className={styles.kicker}>{nachricht.kicker}</span>
					<h2 className={styles.title}>{nachricht.titel}</h2>
					<p className={styles.lead}>{nachricht.text}</p>

					<div className={styles.actions}>
						<Link to={nachricht.ziel} className={styles.cta}>
							{nachricht.cta}
						</Link>
						<span className={styles.meta}>{nachricht.meta}</span>
					</div>
				</div>

				<div className={styles.art}>
					<picture>
						<source srcSet={nachricht.bild.avif} type="image/avif" />
						<img src={nachricht.bild.webp} alt="" />
					</picture>
					{nachricht.kiHinweis && <span className={styles.ki}>{t('AI-generated')}</span>}
					<span className={styles.chipTwo}>{nachricht.marke}</span>
				</div>
			</div>

			{/*
			 * Die Punkte sind Knöpfe, keine Zierde: Wer die andere Nachricht sehen
			 * will, soll nicht neun Sekunden warten müssen.
			 */}
			<div className={styles.punkte} role="tablist" aria-label={t('News')}>
				{NACHRICHTEN.map((eintrag, index) => (
					<button
						key={eintrag.key}
						type="button"
						role="tab"
						aria-selected={index === aktiv}
						aria-label={eintrag.punkt}
						className={clsx(styles.punkt, index === aktiv && styles.punktAktiv)}
						onClick={() => setAktiv(index)}
					/>
				))}
			</div>
		</section>
	)
}
